/**
 * StorageDashboard — Lager detail view with stock levels, movement history,
 * delivery form, and inventory correction.
 * Phase 1 Sprint 3: Lager-Management.
 *
 * Props:
 *   token — JWT
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { AnimatedList } from './motion';

function useCategoryLabels() {
  const { t } = useTranslation();
  return useMemo(() => ({
    all: t('weekly.filters.all'),
    fertilizer: t('storage.categories.fertilizer'),
    psm: t('storage.categories.psm'),
    seed: t('storage.categories.seed'),
    other: t('storage.categories.other'),
  }), [t]);
}

const MOVEMENT_ICONS = {
  IN: '📥',
  OUT: '📤',
  RETURN: '↩️',
  CORRECTION: '🔧',
};

export default function StorageDashboard({ token }) {
  const { t } = useTranslation();
  const categoryLabels = useCategoryLabels();
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showInventory, setShowInventory] = useState(false);

  const fetchStock = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch('/api/v1/aif/storage/stock', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStock(await res.json());
    } catch (_) { /* network error — silent */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchStock(); }, [fetchStock]);

  const fetchMovements = async (productName) => {
    setMovementsLoading(true);
    try {
      const res = await fetch(`/api/v1/aif/storage/stock/${encodeURIComponent(productName)}/movements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMovements(data.movements || []);
      }
    } catch (_) { /* network error — silent */ }
    setMovementsLoading(false);
  };

  const handleProductClick = (productName) => {
    if (selectedProduct === productName) {
      setSelectedProduct(null);
      setMovements([]);
    } else {
      setSelectedProduct(productName);
      fetchMovements(productName);
    }
  };

  const items = (stock?.items || []).filter(i => filter === 'all' || i.category === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-7 h-7 border-4 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 px-4 py-4" data-testid="storage-dashboard">
      {/* Summary header */}
      <div className="bg-[var(--aif-bg-elevated)] rounded-2xl p-4">
        <div className="text-2xl font-bold text-[var(--aif-text-primary)]">
          {stock?.total_value_eur?.toLocaleString('de-DE')} EUR
        </div>
        <div className="text-xs text-[var(--aif-text-muted)] mt-0.5">
          {stock?.product_count || 0} {t('storage.productCount')} · {t('storage.totalValue')}
        </div>
        {stock?.low_stock_count > 0 && (
          <div className="mt-2 text-xs text-orange-500 font-medium">
            {stock.low_stock_count} {t('storage.lowStockWarning')}
          </div>
        )}
      </div>

      {/* Category filter chips */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-none">
        {Object.entries(categoryLabels).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition flex-shrink-0
              ${filter === key
                ? 'bg-[var(--aif-color-primary)] text-white'
                : 'bg-[var(--aif-bg-elevated)] text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-page)]'
              }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Product list */}
      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-[var(--aif-text-muted)]">
          {t('storage.noItems')}
        </div>
      ) : (
      <AnimatedList className="space-y-2">
        {items.map((item) => {
          // Percentage: ratio of current to max observed (last IN quantity or 5x threshold)
          const capacity = item.low_stock_threshold > 0 ? item.low_stock_threshold * 5 : item.current_quantity;
          const pct = capacity > 0 ? Math.min(100, (item.current_quantity / capacity) * 100) : 50;
          // Color based on stock level vs threshold
          const barColor = item.low_stock_warning
            ? (pct < 10 ? '#ef4444' : '#f59e0b')   // red if < 10%, amber if warning
            : '#22c55e';                             // green if OK
          const isExpanded = selectedProduct === item.product_name;

          return (
            <div key={item.product_name}>
              <button
                onClick={() => handleProductClick(item.product_name)}
                className="w-full text-left bg-[var(--aif-bg-elevated)] rounded-xl p-3 hover:bg-[var(--aif-bg-page)] transition"
                data-testid={`stock-item-${item.product_name}`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-[var(--aif-text-primary)]">
                    {item.low_stock_warning && '\u26a0\ufe0f '}{item.product_name}
                  </span>
                  <span className="text-sm font-semibold text-[var(--aif-text-primary)]">
                    {item.current_quantity.toLocaleString('de-DE')} {item.unit}
                  </span>
                </div>
                {/* Progress bar */}
                <div className="h-2 bg-[var(--aif-border-default)] rounded-full overflow-hidden mb-1">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: barColor }}
                  />
                </div>
                <div className="text-[10px] text-[var(--aif-text-muted)]">
                  {item.value_eur.toLocaleString('de-DE')} EUR
                </div>
              </button>

              {/* Movement history (expanded) */}
              {isExpanded && (
                <div className="mt-1 ml-3 mr-1 border-l-2 border-[var(--aif-border-default)] pl-3 pb-2">
                  {movementsLoading ? (
                    <div className="text-[10px] text-[var(--aif-text-muted)] py-2">{t('ui.loading')}</div>
                  ) : movements.length === 0 ? (
                    <div className="text-[10px] text-[var(--aif-text-muted)] py-2">{t('ui.noMovements')}</div>
                  ) : (
                    movements.slice(0, 10).map((m) => (
                      <div key={m.id} className="flex items-start gap-2 py-1.5 text-[10px]">
                        <span>{MOVEMENT_ICONS[m.movement_type] || '📦'}</span>
                        <div className="flex-1 min-w-0">
                          <span className={`font-medium ${m.quantity > 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {m.quantity > 0 ? '+' : ''}{m.quantity.toLocaleString('de-DE')} {m.unit}
                          </span>
                          {m.source_task_title && (
                            <span className="text-[var(--aif-text-muted)] ml-1">— {m.source_task_title}</span>
                          )}
                          {m.notes && <span className="text-[var(--aif-text-muted)] ml-1">({m.notes})</span>}
                        </div>
                        <span className="text-[var(--aif-text-muted)] flex-shrink-0">
                          {m.created_at ? new Date(m.created_at).toLocaleDateString('de-DE') : ''}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </AnimatedList>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 pt-2">
        <button
          onClick={() => { setShowAddForm(!showAddForm); setShowInventory(false); }}
          className="flex-1 py-2.5 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium hover:opacity-90 transition"
          data-testid="storage-add-delivery"
        >
          + {t('storage.addDelivery')}
        </button>
        <button
          onClick={() => { setShowInventory(!showInventory); setShowAddForm(false); }}
          className="flex-1 py-2.5 rounded-xl border border-[var(--aif-border-default)] text-sm font-medium text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-elevated)] transition"
          data-testid="storage-inventory-btn"
        >
          {t('storage.inventory')}
        </button>
      </div>

      {/* Add delivery form */}
      {showAddForm && (
        <AddDeliveryForm token={token} onSaved={() => { setShowAddForm(false); fetchStock(); }} onCancel={() => setShowAddForm(false)} />
      )}

      {/* Inventory correction form */}
      {showInventory && stock?.items?.length > 0 && (
        <InventoryForm token={token} items={stock.items} onSaved={() => { setShowInventory(false); fetchStock(); }} onCancel={() => setShowInventory(false)} />
      )}
    </div>
  );
}

function AddDeliveryForm({ token, onSaved, onCancel }) {
  const { t } = useTranslation();
  const [productName, setProductName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('kg');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!productName.trim() || !quantity) return;
    setSaving(true);
    try {
      await apiClient.post('/api/v1/aif/storage/movements', {
        product_name: productName.trim(),
        movement_type: 'IN',
        quantity: parseFloat(quantity),
        unit,
        notes: notes.trim() || null,
      }, { queueOnOffline: true });
      onSaved();
    } catch (_) { /* network error — silent */ }
    setSaving(false);
  };

  const inputCls = `w-full px-3 py-2 rounded-xl border border-[var(--aif-border-default)]
                    bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                    focus:outline-none focus-visible:ring-1 focus:ring-[var(--aif-color-primary)] min-h-[44px]`;

  return (
    <div className="bg-[var(--aif-bg-elevated)] rounded-xl p-4 space-y-3 border border-[var(--aif-border-default)]" data-testid="delivery-form">
      <h3 className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('storage.addDelivery')}</h3>
      <input
        type="text" value={productName} onChange={e => setProductName(e.target.value)}
        placeholder={t('ui.productPlaceholder')} className={inputCls} data-testid="delivery-product"
      />
      <div className="grid grid-cols-[1fr_80px] gap-2">
        <input type="number" value={quantity} onChange={e => setQuantity(e.target.value)}
          placeholder={t('ui.quantityPlaceholder')} className={inputCls} data-testid="delivery-quantity" />
        <select value={unit} onChange={e => setUnit(e.target.value)} className={inputCls}>
          <option value="kg">kg</option><option value="l">l</option><option value="t">t</option>
        </select>
      </div>
      <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
        placeholder={t('ui.supplierPlaceholder')} className={inputCls} />
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 py-2 rounded-xl border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-secondary)] min-h-[44px]">
          {t('ui.cancel')}
        </button>
        <button onClick={handleSave} disabled={saving || !productName.trim() || !quantity}
          className="flex-1 py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium disabled:opacity-50 min-h-[44px]" data-testid="delivery-save">
          {saving ? t('form.saving') : t('storage.bookIn')}
        </button>
      </div>
    </div>
  );
}

function InventoryForm({ token, items, onSaved, onCancel }) {
  const { t } = useTranslation();
  const [quantities, setQuantities] = useState(() => {
    const init = {};
    for (const item of items) {
      init[item.product_name] = String(item.current_quantity);
    }
    return init;
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (product, value) => {
    setQuantities(prev => ({ ...prev, [product]: value }));
  };

  const getDiff = (item) => {
    const actual = parseFloat(quantities[item.product_name] || '0');
    return actual - item.current_quantity;
  };

  const handleSave = async () => {
    const correctionItems = items
      .filter(item => Math.abs(getDiff(item)) >= 0.1)
      .map(item => ({
        product_name: item.product_name,
        actual_quantity: parseFloat(quantities[item.product_name] || '0'),
        unit: item.unit,
      }));

    if (correctionItems.length === 0) {
      onSaved();
      return;
    }

    setSaving(true);
    try {
      await apiClient.post('/api/v1/aif/storage/inventory', { items: correctionItems, notes: t('storage.inventory') }, { queueOnOffline: true });
      onSaved();
    } catch (_) { /* network error — silent */ }
    setSaving(false);
  };

  const inputCls = `w-full px-3 py-2 rounded-xl border border-[var(--aif-border-default)]
                    bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                    focus:outline-none focus-visible:ring-1 focus:ring-[var(--aif-color-primary)] min-h-[44px]`;

  return (
    <div className="bg-[var(--aif-bg-elevated)] rounded-xl p-4 space-y-3 border border-[var(--aif-border-default)]" data-testid="inventory-form">
      <h3 className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('storage.inventory')}</h3>
      <div className="space-y-2">
        {items.map(item => {
          const diff = getDiff(item);
          return (
            <div key={item.product_name} className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-[var(--aif-text-primary)] truncate">{item.product_name}</div>
                <div className="text-[10px] text-[var(--aif-text-muted)]">
                  {t('storage.expected')}: {item.current_quantity.toLocaleString('de-DE')} {item.unit}
                </div>
              </div>
              <div className="w-24">
                <input
                  type="number"
                  value={quantities[item.product_name]}
                  onChange={e => handleChange(item.product_name, e.target.value)}
                  className={inputCls}
                  data-testid={`inventory-qty-${item.product_name}`}
                />
              </div>
              <div className="w-16 text-right">
                {Math.abs(diff) >= 0.1 && (
                  <span className={`text-xs font-medium ${diff > 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {diff > 0 ? '+' : ''}{diff.toLocaleString('de-DE')}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onCancel} className="flex-1 py-2 rounded-xl border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-secondary)] min-h-[44px]">
          {t('ui.cancel')}
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex-1 py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium disabled:opacity-50 min-h-[44px]" data-testid="inventory-save">
          {saving ? t('form.saving') : t('storage.saveInventory')}
        </button>
      </div>
    </div>
  );
}
